import * as filterSetService from './components/ProjectsFiltersBar/FilterSet/filter-set.service';
import {
  ON_CHANGE_FILTERS,
  ON_CHANGE_FILTERS_BAR_HEIGHT,
  ON_CHANGE_LOCAL_FILTERS,
  ON_CLEAR_FILTER_SET,
  ON_CREATE_FILTER_SET,
  ON_DELETE_FILTER_SET,
  ON_INIT_FILTERS,
  ON_SELECT_FILTER_SET,
  ON_SELECT_PERIOD,
  ON_UPDATE_FILTER_SET,
  ON_UPDATE_FILTERS_PAYLOAD,
  RESET,
  SET_IS_LOADING,
  SET_UI_REF
} from './actions';
import { useContext } from 'react';
import useIsTreeHierarchyEnabled from './hooks/useIsTreeHierarchyEnabled';
import { syncFiltersByTreeHierarchy } from './components/ProjectsFiltersBar/FilterSet/filter-set.utils';
import usePmSensorAuthId from 'components/hooks/PM/usePmSensorAuthId';
import { isEqual } from 'lodash';

const useContextActions = ({ Context }) => {
  const { dispatch } = useContext(Context);
  const isTreeHierarchyEnabled = useIsTreeHierarchyEnabled(Context);
  const pmSensorAuthId = usePmSensorAuthId();

  const setIsLoading = isLoading =>
    dispatch({ type: SET_IS_LOADING, payload: isLoading });

  const syncFilters = async (filters, filterSetId = null) => {
    try {
      setIsLoading(true);
      const syncedFilters = await syncFiltersByTreeHierarchy(
        filters,
        isTreeHierarchyEnabled,
        pmSensorAuthId
      );
      if (filterSetId && !isEqual(filters, syncedFilters)) {
        onUpdateFilterSet(filterSetId, { filters: syncedFilters });
      }
      setIsLoading(false);
      return syncedFilters;
    } catch (e) {
      setIsLoading(false);
      return filters;
    }
  };

  const reset = () =>
    dispatch({
      type: RESET
    });

  const onSelectPeriod = period =>
    dispatch({ type: ON_SELECT_PERIOD, payload: period });

  const onInitFilters = async ({
    period,
    setPeriod,
    filters,
    pdtEntities = [],
    status,
    filterSets,
    selectedFilterSetId,
    extraPayload
  } = {}) => {
    const syncedFilters = await syncFilters(filters, selectedFilterSetId);

    dispatch({
      type: ON_INIT_FILTERS,
      payload: {
        period,
        setPeriod,
        filters: syncedFilters,
        pdtEntities,
        status,
        filterSets,
        selectedFilterSetId,
        extraPayload
      }
    });
  };

  const onChangeFilters = filters =>
    dispatch({
      type: ON_CHANGE_FILTERS,
      payload: filters
    });

  const onChangeLocalFilters = filters =>
    dispatch({
      type: ON_CHANGE_LOCAL_FILTERS,
      payload: filters
    });

  const onUpdateFiltersPayload = filtersPayload =>
    dispatch({
      type: ON_UPDATE_FILTERS_PAYLOAD,
      payload: filtersPayload
    });

  const onSelectFilterSet = async (filterSet = {}) => {
    const { id, filters } = filterSet;
    const syncedFilters = await syncFilters(filters, id);

    dispatch({
      type: ON_SELECT_FILTER_SET,
      payload: {
        filterSetId: id,
        filters: syncedFilters
      }
    });
  };

  const onClearFilterSet = () =>
    dispatch({
      type: ON_CLEAR_FILTER_SET
    });

  const onUpdateFilterSet = async (filterSetId, payload) => {
    try {
      const updatedFilterSetRes = await filterSetService.updateFilterSet(
        filterSetId,
        payload
      );
      dispatch({
        type: ON_UPDATE_FILTER_SET,
        payload: updatedFilterSetRes
      });
    } catch (e) {}
  };

  const onCreateFilterSet = async payload => {
    try {
      const newFilterSetRes = await filterSetService.createFilterSet(payload);
      dispatch({
        type: ON_CREATE_FILTER_SET,
        payload: newFilterSetRes
      });
      return newFilterSetRes;
    } catch (e) {}
  };

  const onDeleteFilterSet = async filterSetId => {
    try {
      await filterSetService.deleteFilterSet(filterSetId);
      dispatch({
        type: ON_DELETE_FILTER_SET,
        payload: filterSetId
      });
    } catch (e) {}
  };

  const setUiRef = (uiRefKey, ref) =>
    dispatch({
      type: SET_UI_REF,
      payload: {
        uiRefKey,
        ref
      }
    });

  const onChangeFiltersBarHeight = height =>
    dispatch({
      type: ON_CHANGE_FILTERS_BAR_HEIGHT,
      payload: height
    });

  return {
    setIsLoading,

    reset,
    onSelectPeriod,

    onInitFilters,
    onChangeFilters,
    onChangeLocalFilters,
    onUpdateFiltersPayload,

    onSelectFilterSet,
    onClearFilterSet,
    onUpdateFilterSet,
    onCreateFilterSet,
    onDeleteFilterSet,

    setUiRef,
    onChangeFiltersBarHeight
  };
};

export default useContextActions;
